import { useId, useMemo, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { FORMULA_FUNCTIONS } from '@/pages/workflow/helper/functional-control-flow';
import { cn } from '@/lib/utils';

/**
 * A one-line formula box (`round(score * 100) / 100`, `n % 15 == 0`) with
 * suggestions for the word at the caret: upstream fields, earlier outputs,
 * and function names. Shared by the Calculate panel and the condition
 * builders' "formula" rows. Parsing happens on the backend; this component
 * only edits text and shows the problem it is given, with the position marked.
 */

export interface FormulaSuggestion {
  value: string;
  kind: 'field' | 'output' | 'function';
}

export const FUNCTION_SUGGESTIONS: FormulaSuggestion[] = [
  ...Object.keys(FORMULA_FUNCTIONS),
  'map',
  'filter',
  'find',
  'any',
  'all',
  'exists',
  'is_null',
]
  .sort()
  .map((value) => ({ value, kind: 'function' as const }));

export const fieldSuggestionsToFormula = (
  fields: readonly string[],
): FormulaSuggestion[] =>
  fields
    .filter((field) => field !== 'value' && !field.startsWith('('))
    .map((value) => ({ value, kind: 'field' as const }));

function useDraft<T>(source: T): [T, (next: T) => void] {
  const [draft, setDraft] = useState(source);
  const [seen, setSeen] = useState(source);
  if (seen !== source) {
    setSeen(source);
    setDraft(source);
  }
  return [draft, setDraft];
}

/** The identifier being typed at the caret (`ro` in `a + ro|`), and where it starts. */
const currentToken = (
  text: string,
  caret: number,
): { word: string; start: number } => {
  let start = caret;
  while (start > 0 && /[A-Za-z0-9_.]/.test(text[start - 1])) start -= 1;
  return { word: text.slice(start, caret), start };
};

export interface FormulaProblem {
  message: string;
  hint?: string | null;
  position?: number | null;
}

interface FormulaInputProps {
  value: string;
  onCommit: (text: string) => void;
  suggestions: FormulaSuggestion[];
  disabled?: boolean;
  placeholder?: string;
  problem?: FormulaProblem | null;
  className?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

export function FormulaInput({
  value,
  onCommit,
  suggestions,
  disabled = false,
  placeholder = 'formula',
  problem = null,
  className,
  'aria-label': ariaLabel = 'Formula',
  'data-testid': testId = 'formula-input',
}: FormulaInputProps) {
  const [draft, setDraft] = useDraft(value);
  const [caret, setCaret] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const commit = () => {
    setOpen(false);
    if (draft !== value) onCommit(draft);
  };

  const token = caret === null ? null : currentToken(draft, caret);
  const matches = useMemo(() => {
    if (!open || !token || token.word.length === 0) return [];
    const lower = token.word.toLowerCase();
    return suggestions
      .filter((s) => s.value.toLowerCase().startsWith(lower))
      .filter((s) => s.value !== token.word)
      .slice(0, 8);
  }, [open, token, suggestions]);

  const insert = (suggestion: FormulaSuggestion) => {
    if (!token) return;
    const replacement =
      suggestion.kind === 'function'
        ? `${suggestion.value}(`
        : suggestion.value;
    const next =
      draft.slice(0, token.start) +
      replacement +
      draft.slice(token.start + token.word.length);
    setDraft(next);
    setOpen(false);
    const position = token.start + replacement.length;
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(position, position);
      setCaret(position);
    });
  };

  const position =
    problem && typeof problem.position === 'number' ? problem.position : null;

  return (
    <div className='min-w-0 flex-1'>
      <div className='relative'>
        <Input
          ref={inputRef}
          value={draft}
          placeholder={placeholder}
          aria-label={ariaLabel}
          aria-autocomplete='list'
          aria-controls={open && matches.length > 0 ? listId : undefined}
          data-testid={testId}
          disabled={disabled}
          spellCheck={false}
          autoComplete='off'
          className={cn(
            'h-8 text-xs font-mono',
            problem && 'border-destructive',
            className,
          )}
          onChange={(event) => {
            setDraft(event.target.value);
            setCaret(event.target.selectionStart);
            setOpen(true);
            setHighlight(0);
          }}
          onClick={(event) =>
            setCaret((event.target as HTMLInputElement).selectionStart)
          }
          onKeyUp={(event) => {
            if (
              event.key === 'ArrowLeft' ||
              event.key === 'ArrowRight' ||
              event.key === 'Home' ||
              event.key === 'End'
            ) {
              setCaret((event.target as HTMLInputElement).selectionStart);
            }
          }}
          onBlur={() => {
            // Let a click on a suggestion land first.
            window.setTimeout(commit, 120);
          }}
          onKeyDown={(event) => {
            if (open && matches.length > 0) {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setHighlight((h) => (h + 1) % matches.length);
                return;
              }
              if (event.key === 'ArrowUp') {
                event.preventDefault();
                setHighlight((h) => (h - 1 + matches.length) % matches.length);
                return;
              }
              if (event.key === 'Tab') {
                event.preventDefault();
                insert(matches[highlight]);
                return;
              }
              if (event.key === 'Escape') {
                setOpen(false);
                return;
              }
            }
            if (event.key === 'Enter') {
              event.preventDefault();
              if (open && matches.length > 0) insert(matches[highlight]);
              else commit();
            }
          }}
        />
        {open && matches.length > 0 && !disabled && (
          <ul
            id={listId}
            role='listbox'
            data-testid={`${testId}-suggestions`}
            className='absolute z-20 mt-1 w-full max-h-48 overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md text-xs'
          >
            {matches.map((s, i) => (
              <li
                key={`${s.kind}:${s.value}`}
                role='option'
                aria-selected={i === highlight}
                className={cn(
                  'flex items-center justify-between gap-2 px-2 py-1 cursor-pointer font-mono',
                  i === highlight && 'bg-accent text-accent-foreground',
                )}
                onMouseDown={(event) => {
                  event.preventDefault();
                  insert(s);
                }}
                onMouseEnter={() => setHighlight(i)}
              >
                <span>
                  {s.value}
                  {s.kind === 'function' ? '()' : ''}
                </span>
                <span className='text-[10px] text-muted-foreground font-sans'>
                  {s.kind}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {problem && (
        <div
          className='mt-1 text-xs text-destructive'
          data-testid={`${testId}-error`}
        >
          {position !== null && draft === value && value.length > 0 && (
            <pre className='font-mono whitespace-pre overflow-x-auto text-[11px] leading-tight mb-0.5 text-muted-foreground'>
              {value}
              {'\n'}
              {' '.repeat(Math.min(position, value.length))}
              <span className='text-destructive'>^</span>
            </pre>
          )}
          <p>
            {problem.message}
            {position !== null ? ` (at character ${position + 1})` : ''}
          </p>
          {problem.hint && (
            <p className='text-muted-foreground'>{problem.hint}</p>
          )}
        </div>
      )}
    </div>
  );
}
